/**
 * useOwnerSettings Hook
 * 오너 설정 상태 관리
 */
import { useState, useEffect, useCallback } from 'react';
import { OrganizationInfo } from '../models/OwnerSettings';
import { ownerSettingsRepository } from '../repositories/OwnerSettingsRepository';
import { ownerSettingsService } from '../services/OwnerSettingsService';
import { useUserStore } from '../../../store/userStore';
import { useAuthStore } from '../../auth/stores/AuthStore';
import { usePlanFeatures } from '../hooks/usePlanFeatures';

export const useOwnerSettings = () => {
  const { currentUser: legacyUser } = useUserStore();
  const { user: authUser } = useAuthStore();

  // AuthStore의 user를 User 타입으로 변환
  const convertedAuthUser = authUser ? {
    id: authUser.id,
    user_number: authUser.id,
    name: authUser.name,
    phone: '',
    email: authUser.email,
    is_active: authUser.isActive,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    role: authUser.role,
    organization_id: authUser.organization_id,
    org_id: authUser.organization_id, // alias for compatibility
    business_id: authUser.organization_id, // alias for compatibility
    organization_name: authUser.organization_name,
  } : null;

  // AuthStore 우선, 없으면 userStore 사용
  const currentUser = convertedAuthUser || legacyUser;

  // 디버깅 로그
  console.log('🔍 useOwnerSettings 디버깅:', {
    authUser,
    convertedAuthUser,
    legacyUser,
    finalCurrentUser: currentUser,
    localStorage_org_id: localStorage.getItem('organization_id'),
  });
  const [orgInfo, setOrgInfo] = useState<OrganizationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [orgDescription, setOrgDescription] = useState('');
  const [showPasswordChange, setShowPasswordChange] = useState(false);

  /**
   * 조직 정보 로드
   */
  const loadOrganizationInfo = useCallback(async () => {
    try {
      setLoading(true);

      // 1순위: 캐시된 조직 정보 먼저 확인 (새로고침 대응)
      const cachedOrgInfo = ownerSettingsService.getCachedOrganizationInfo();
      if (cachedOrgInfo) {
        setOrgInfo(cachedOrgInfo);
        setOrgName(cachedOrgInfo.name);
        setOrgDescription(cachedOrgInfo.description || '');

        // 백그라운드에서 최신 정보 가져오기 (캐시 업데이트용)
        const orgId = ownerSettingsService.extractOrganizationId(currentUser);
        if (orgId !== 'org_unknown') {
          ownerSettingsRepository.getOrganization(String(orgId))
            .then(latestInfo => {
              ownerSettingsService.cacheOrganizationInfo(latestInfo);
            })
            .catch(err => console.log('⚠️ 백그라운드 업데이트 실패:', err));
        }
        return;
      }

      // 2순위: API에서 실제 조직 정보 가져오기
      const orgId = ownerSettingsService.extractOrganizationId(currentUser);
      console.log('🔍 조직 ID 디버깅:', {
        currentUser,
        organization_id: currentUser?.organization_id,
        org_id: currentUser?.org_id,
        business_id: currentUser?.business_id,
        finalOrgId: orgId,
      });

      if (orgId !== 'org_unknown') {
        try {
          const organizationInfo = await ownerSettingsRepository.getOrganization(String(orgId));

          // 조직 정보를 localStorage에 캐싱
          ownerSettingsService.cacheOrganizationInfo(organizationInfo);

          setOrgInfo(organizationInfo);
          setOrgName(organizationInfo.name);
          setOrgDescription(organizationInfo.description || '');
          return;
        } catch (apiError) {
          console.warn('⚠️ API 조회 실패:', apiError);
        }
      }

      // 최종 폴백: localStorage 또는 currentUser에서 가져오기
      const savedOrgName = ownerSettingsService.getSavedOrganizationName();
      const tempOrgInfo = ownerSettingsService.createTempOrganizationInfo(
        orgId,
        currentUser,
        savedOrgName
      );

      setOrgInfo(tempOrgInfo);
      setOrgName(tempOrgInfo.name);
      setOrgDescription(tempOrgInfo.description || '');
    } catch (error) {
      console.error('조직 정보 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  /**
   * 조직 ID 복사
   */
  const handleCopyOrgId = useCallback(async () => {
    if (orgInfo) {
      const success = await ownerSettingsService.copyToClipboard(orgInfo.id.toString());
      if (success) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  }, [orgInfo]);

  /**
   * 설정 저장
   */
  const handleSaveSettings = useCallback(async () => {
    if (!orgInfo) return;

    try {
      // API를 통해 조직 정보 업데이트
      const updatedOrg = await ownerSettingsRepository.updateOrganization({
        name: orgName,
        description: orgDescription,
      });

      // 로컬 상태 업데이트
      setOrgInfo({
        ...orgInfo,
        name: updatedOrg.name,
        description: updatedOrg.description,
      });

      // localStorage 업데이트
      ownerSettingsService.updateOrganizationNameInStorage(updatedOrg.name);

      // userStore 업데이트
      if (currentUser) {
        const updatedUser = {
          ...currentUser,
          organization_name: updatedOrg.name,
        };

        useUserStore.setState({
          currentUser: updatedUser,
        });

        ownerSettingsService.updateUserStorageOrganizationName(updatedOrg.name);
      }

      // 사이드바 리렌더링 이벤트 발생
      ownerSettingsService.dispatchOrganizationUpdateEvent();

      alert('설정이 저장되었습니다.');
    } catch (error) {
      console.error('설정 저장 실패:', error);
      alert('설정 저장에 실패했습니다.');
    }
  }, [orgInfo, orgName, orgDescription, currentUser]);

  /**
   * 비밀번호 변경 모달 열기
   */
  const openPasswordChangeModal = useCallback(() => {
    setShowPasswordChange(true);
  }, []);

  /**
   * 비밀번호 변경 모달 닫기
   */
  const closePasswordChangeModal = useCallback(() => {
    setShowPasswordChange(false);
  }, []);

  /**
   * 비밀번호 변경 성공 처리
   */
  const handlePasswordChangeSuccess = useCallback(() => {
    setShowPasswordChange(false);
    alert('비밀번호가 성공적으로 변경되었습니다.');
  }, []);

  // 플랜 정보 가져오기
  const { permissions, loading: planLoading } = usePlanFeatures();

  // 초기 데이터 로드
  useEffect(() => {
    loadOrganizationInfo();
  }, [loadOrganizationInfo]);

  return {
    orgInfo,
    loading,
    copied,
    orgName,
    setOrgName,
    orgDescription,
    setOrgDescription,
    showPasswordChange,
    handleCopyOrgId,
    handleSaveSettings,
    openPasswordChangeModal,
    closePasswordChangeModal,
    handlePasswordChangeSuccess,
    // 플랜 정보
    planInfo: permissions,
    planLoading,
  };
};
